import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { trackWatchlistEvent, WATCHLIST_EVENTS } from '../../watchlist/constants';
import useMonitorAlerts from '../hooks/useMonitorAlerts';
import { dismissAlerts, dismissArrival, hideRunningToast, markAllRead, markRead, snoozeArrival } from '../store';
import { TRANSLATION_KEY, WATCHLIST_PATH } from '../constants';
import { buildWatchlistHash, countKey } from '../utils';
import MonitorGroupedToast from './MonitorGroupedToast';
import MonitorResultToast from './MonitorResultToast';
import css from './MonitorAlertsToasts.css';

const TOAST_KEY = `${TRANSLATION_KEY}.TOASTS`;

const RunningToast = ({ count, onHide }) => (
    <div className={css.runningCard}>
        <span className={css.spinner} />
        <span className={css.runningBody}>
            <span className={css.runningTitle}>
                <Translate id={`${TOAST_KEY}.${countKey('CHECKING', count)}`} data={{ count }} />
            </span>
            <span className={css.runningSubtitle}>
                <Translate id={`${TOAST_KEY}.CHECKING_SUBTITLE`} />
            </span>
        </span>
        <button type="button" className={css.hideButton} onClick={onHide}>
            <Translate id={`${TOAST_KEY}.HIDE`} />
        </button>
    </div>
);

RunningToast.propTypes = {
    count: PropTypes.number.isRequired,
    onHide: PropTypes.func.isRequired,
};

const goToWatchlist = sessionId => {
    window.location.hash = buildWatchlistHash(WATCHLIST_PATH, sessionId);
};

const MonitorAlertsToasts = () => {
    const { cardAlerts, checkingCount, runningToastHidden, stopped } = useMonitorAlerts();

    // Once the feed has stopped nothing is left to report a finish, so a progress card
    // would spin until the page is reloaded.
    const checking = checkingCount > 0 && !runningToastHidden && !stopped;
    // The corner holds one card, so a run alongside a result is reported along the bottom
    // of that card instead.
    const showRunning = checking && cardAlerts.length === 0;
    const inCardChecking = checking && cardAlerts.length > 0 ? checkingCount : 0;

    const openAlert = alert => {
        trackWatchlistEvent(WATCHLIST_EVENTS.ALERT_CLICKED, { monitor_id: alert.monitorId });
        markRead(alert.monitorId);
        goToWatchlist(alert.sessionId);
    };

    // Not markRead: that dismisses the arrival and takes the row the user just opened
    // out of the card.
    const expandAlert = alert => trackWatchlistEvent(WATCHLIST_EVENTS.ALERT_EXPANDED, { monitor_id: alert.monitorId });

    const [first] = cardAlerts;

    // Mounted empty: assistive tech announces mutations of a region it was already
    // watching, not one inserted together with its first card.
    return (
        <div className={css.stack} role="status" aria-live="polite">
            {cardAlerts.length > 1 && (
                <MonitorGroupedToast
                    alerts={cardAlerts}
                    checkingCount={inCardChecking}
                    onDismiss={() => dismissAlerts(cardAlerts.map(alert => alert.monitorId))}
                    onMarkAllRead={() => {
                        trackWatchlistEvent(WATCHLIST_EVENTS.ALERTS_MARK_ALL_READ, { count: cardAlerts.length });
                        markAllRead();
                    }}
                    onOpen={() => {
                        dismissAlerts(cardAlerts.map(alert => alert.monitorId));
                        goToWatchlist(null);
                    }}
                    onOpenAlert={openAlert}
                    onExpandAlert={expandAlert}
                />
            )}
            {cardAlerts.length === 1 && (
                <MonitorResultToast
                    // Without a key React reuses the instance across two alerts and the
                    // previous card's expanded state carries onto the next result.
                    key={first.monitorId}
                    alert={first}
                    checkingCount={inCardChecking}
                    onDismiss={() => dismissArrival(first.monitorId)}
                    onSnooze={() => {
                        trackWatchlistEvent(WATCHLIST_EVENTS.ALERT_SNOOZED, { monitor_id: first.monitorId });
                        snoozeArrival(first.monitorId, Date.now());
                    }}
                    onOpen={() => {
                        dismissArrival(first.monitorId);
                        goToWatchlist(null);
                    }}
                    onOpenThread={() => openAlert(first)}
                />
            )}
            {showRunning && <RunningToast count={checkingCount} onHide={hideRunningToast} />}
        </div>
    );
};

export default MonitorAlertsToasts;
